import React, { useState } from "react";
import { signInWithEmailAndPassword, signInWithPopup, signInAnonymously } from "firebase/auth";
import { auth, googleProvider } from "../../firebase";
import axios from "axios";
import Header from "../Header";
import Footer from "../Footer";
import "./Auth.css";

function Login(props) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [localLoading, setLocalLoading] = useState(false);
  const [error, setError] = useState("");

  const combinedLoading = props.isLoading || localLoading;

  // Enhanced debugging: Check ALL props received
  console.log("🔍 [Login.jsx] ALL props received:", {
    onGuestLogin: props.onGuestLogin,
    typeofOnGuestLogin: typeof props.onGuestLogin,
    hasOnGuestLogin: 'onGuestLogin' in props,
    propsKeys: Object.keys(props),
    fullProps: props
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      setLocalLoading(true);
      setError("");
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const token = await userCredential.user.getIdToken();
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/auth/login`, { token });
      sessionStorage.setItem("token", token);
      sessionStorage.setItem("user", JSON.stringify(res.data));
      props.onLoginSuccess(res.data);
    } catch (err) {
      // Security: Always show generic error for auth failures
      if (err.code && (err.code.startsWith('auth/') || err.message.includes('auth/'))) {
        setError("Invalid email or password");
      } else {
        setError("Login failed. Please try again.");
      }
    } finally {
      setLocalLoading(false);
    }
  };

  // Direct guest login function as backup
  const handleGuestLoginDirect = async () => {
    console.log("⚡ [Login.jsx] Direct guest login invoked");
    try {
      setLocalLoading(true);
      setError("");
      const userCredential = await signInAnonymously(auth);
      const token = await userCredential.user.getIdToken();
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/auth/login`, {
        token,
        isAnonymous: true
      });
      sessionStorage.setItem("token", token);
      sessionStorage.setItem("user", JSON.stringify(res.data));

      if (props.onLoginSuccess) {
        props.onLoginSuccess(res.data);
      }
    } catch (err) {
      console.error("❌ [Login.jsx] Direct guest login error:", err);
      // Generic error for guest login failure
      setError("Guest access failed. Please try again.");
    } finally {
      setLocalLoading(false);
    }
  };

  // Unified guest login handler
  const handleGuestLoginClick = async () => {
    console.log("🎯 [Login.jsx] Guest login button clicked");

    // Option 1: Use prop if available and is a function
    if (props.onGuestLogin && typeof props.onGuestLogin === 'function') {
      console.log("✅ [Login.jsx] Using onGuestLogin prop");
      try {
        await props.onGuestLogin();
        return;
      } catch (error) {
        console.error("❌ [Login.jsx] Prop guest login failed, falling back:", error);
        // Fall through to direct method
      }
    } else {
      console.warn("⚠️ [Login.jsx] onGuestLogin prop not available or not a function");
      console.warn("Value of onGuestLogin:", props.onGuestLogin);
    }

    // Option 2: Use direct method as fallback
    console.log("🔄 [Login.jsx] Falling back to direct guest login");
    await handleGuestLoginDirect();
  };

  return (
    <div className="auth-page">
      <div className="auth-content">
        <Header />
        <div className="auth-wrapper">
          <div className="auth-box">
            <h2>Welcome Back</h2>
            <p className="auth-subtitle">Instant access to your PDFradier</p>

            {error && <div className="error-message">{error}</div>}

            {/* GUEST LOGIN BUTTON - FIXED */}
            <button
              type="button"
              className="guest-auth-button"
              disabled={combinedLoading}
              onClick={handleGuestLoginClick}
              style={{
                backgroundColor: '#10b981',
                color: 'white',
                border: 'none',
                padding: '12px 20px',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: combinedLoading ? 'not-allowed' : 'pointer',
                width: '100%',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                opacity: combinedLoading ? 0.7 : 1
              }}
            >
              <span className="guest-icon">⚡</span>
              {combinedLoading ? "Processing..." : "Guest (Limited AI)"}

            </button>

            <div className="divider"><span>or sign in with</span></div>

            <button
              type="button"
              className="google-auth-button"
              disabled={combinedLoading}
              onClick={async () => {
                try {
                  setLocalLoading(true);
                  const result = await signInWithPopup(auth, googleProvider);
                  const token = await result.user.getIdToken();
                  const res = await axios.post(`${process.env.REACT_APP_API_URL}/auth/login`, { token });
                  sessionStorage.setItem("token", token);
                  sessionStorage.setItem("user", JSON.stringify(res.data));
                  sessionStorage.setItem("user", JSON.stringify(res.data));
                  props.onLoginSuccess(res.data);
                } catch (err) {
                  // Generic error for Google login
                  setError("Google sign-in failed. Please try again.");
                } finally {
                  setLocalLoading(false);
                }
              }}
            >
              <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" /><path fill="#4285F4" d="M46.5 24.5c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" /><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" /><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" /></svg>
              Google
            </button>

            <div className="divider"><span>or email</span></div>

            <form onSubmit={handleLogin}>
              <div className="input-group">
                <input
                  type="email"
                  placeholder="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="auth-input"
                  required
                  disabled={combinedLoading}
                />
              </div>
              <div className="input-group">
                <input
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="auth-input"
                  required
                  disabled={combinedLoading}
                />
              </div>

              <div className="forgot-password-container">
                <button
                  type="button"
                  onClick={props.showForgotPassword}
                  className="forgot-password-link"
                >
                  Forgot your password?
                </button>
              </div>

              <button
                type="submit"
                disabled={combinedLoading}
                className="auth-button"
              >
                {combinedLoading ? "Signing In..." : "Sign In"}
              </button>
            </form>

            <div className="auth-switch">
              <p>Don't have an account?{" "}
                <button
                  type="button"
                  onClick={props.switchToSignup}
                  className="switch-link"
                >
                  Sign up
                </button>
              </p>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </div>
  );
}

export default Login;